'use client'

import { useState } from 'react'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/notion/ui/popover'
import { EditableText } from '../functionality/EditableText'
import {
  BlockBadge,
  BLOCK_BADGE_VARIANTS,
  BADGE_PRESETS,
} from './BlockBadge'

const BADGE_VARIANT_KEYS = Object.keys(BLOCK_BADGE_VARIANTS)

/**
 * Card-style title row — title + badge inline (original .card-title).
 */
export function BlockChromeHeader({
  title = '',
  titleInstanceKey,
  badgeInstanceKey,
  onTitleChange,
  badge,
  onBadgeChange,
  locked = false,
  className = '',
}) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const applyPreset = (preset) => {
    if (!(title ?? '').trim()) return
    onBadgeChange?.({
      text: preset.sample,
      variant: preset.variant,
    })
    setPickerOpen(false)
  }

  const setBadgeVariant = (variant) => {
    const current = badge ?? { text: 'label', variant: 'purple' }
    onBadgeChange?.({ ...current, variant })
    setPickerOpen(false)
  }

  const removeBadge = () => {
    onBadgeChange?.(null)
    setPickerOpen(false)
  }

  const openPicker = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (locked || !onBadgeChange) return
    setPickerOpen(true)
  }

  if (!onTitleChange && !badge?.text && locked) return null

  return (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
      <PopoverAnchor asChild>
        <div
          className={`card-title nb-block-chrome-header ${className}`.trim()}
          onContextMenu={openPicker}
        >
          {onTitleChange ? (
            <EditableText
              instanceKey={
                titleInstanceKey ??
                `header:${String(title).slice(0, 32)}`
              }
              value={title}
              onSave={(val) => {
                const t = (val ?? '').trim()
                if (!t) return
                onTitleChange(t)
              }}
              multiline={false}
              inline
            />
          ) : (
            <span>{title}</span>
          )}
          {badge?.text ? (
            <BlockBadge
              instanceKey={badgeInstanceKey}
              text={badge.text}
              variant={badge.variant}
              onSave={
                onBadgeChange
                  ? (text) => {
                      const t = (text ?? '').trim()
                      if (!t || !(title ?? '').trim()) return
                      onBadgeChange({ ...badge, text: t })
                    }
                  : undefined
              }
              onPress={onBadgeChange ? openPicker : undefined}
            />
          ) : null}
          {!locked && onBadgeChange && !badge?.text ? (
            <button
              type="button"
              className="nb-badge-add-trigger"
              onClick={openPicker}
              aria-label="Add badge"
            >
              + badge
            </button>
          ) : null}
        </div>
      </PopoverAnchor>

      {!locked && onBadgeChange && (
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          avoidCollisions
          className="nb-block-header-menu nb-premium-menu-anim nb-badge-picker notion-surface z-[300] w-auto min-w-[220px] max-w-[min(320px,calc(100vw-24px))] max-h-[320px] overflow-y-auto no-scrollbar rounded-xl border border-white/[0.08] bg-[#141820] p-1.5 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.65)]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Badge presets</div>
          <div className="flex flex-col gap-0.5 pb-2">
            {BADGE_PRESETS.map((preset) => (
              <button
                key={preset.variant + preset.label}
                type="button"
                role="menuitem"
                className="flex w-full cursor-pointer items-center rounded-lg px-2.5 py-2 text-left outline-none hover:bg-white/[0.08] transition-colors"
                onClick={() => applyPreset(preset)}
              >
                <span
                  className={`badge ${BLOCK_BADGE_VARIANTS[preset.variant]}`}
                >
                  {preset.label}
                </span>
              </button>
            ))}
          </div>
          {badge?.text ? (
            <div className="border-t border-white/[0.06] pt-1.5 mt-1.5">
              <button
                type="button"
                role="menuitem"
                className="flex w-full cursor-pointer items-center rounded-lg px-2.5 py-2 text-[13px] text-red-400/90 outline-none hover:bg-red-500/10 transition-colors"
                onClick={removeBadge}
              >
                Remove badge
              </button>
            </div>
          ) : null}
        </PopoverContent>
      )}
    </Popover>
  )
}
